/**
 * Central Firebase target resolution for prmassist-web.
 *
 * A single build-time switch, REACT_APP_FIREBASE_TARGET, decides which Firebase
 * project(s) every app in this bundle points at:
 *
 *   live     ->  prmassist-portal | prmassist1 | prmassist-marketplace
 *   sandbox  ->  prm-sandbox-env (all three apps)
 *   sgs      ->  prmassist-sgs   (all three apps)
 *
 * IMPORTANT: this is resolved at BUILD time, not runtime. Webpack inlines the
 * value, so changing target requires a rebuild:
 *
 *   npm start          / npm run build          -> value from .env
 *   npm run start:sandbox:emulator              -> sandbox + local emulator
 *   npm run build:live                          -> live (ignores .env)
 *
 * An unknown value throws rather than silently falling back to live.
 *
 * The Firebase app names (default, "secondary", "marketplace") are unchanged, so
 * no consumer of these modules needs to change.
 *
 * This must be kept in step with the backend's FIREBASE_TARGET — the web app gets
 * its ID token from one project and the API gateway verifies it against another,
 * so a mismatch 401s every authenticated request.
 */

const TARGET_NAMES = ['live', 'sandbox', 'sgs'];

export const FIREBASE_TARGET = String(process.env.REACT_APP_FIREBASE_TARGET || 'live').trim().toLowerCase();

if (!TARGET_NAMES.includes(FIREBASE_TARGET)) {
  throw new Error(
    `[firebase-projects] Unknown REACT_APP_FIREBASE_TARGET '${FIREBASE_TARGET}'. Expected one of: ${TARGET_NAMES.join(', ')}`
  );
}

// Single web app registration in prm-sandbox-env, shared by all three apps.
const SANDBOX_CONFIG = {
  apiKey: "AIzaSyAm2k9cE1HeCDFpmUEznqPTrD0cuNmyi6Y",
  authDomain: "prm-sandbox-env.firebaseapp.com",
  projectId: "prm-sandbox-env",
  storageBucket: "prm-sandbox-env.firebasestorage.app",
  messagingSenderId: "995915098014",
  appId: "1:995915098014:web:5e7af9438f94c8a67c588f",
  measurementId: "G-FW81X5ZPN6",
};

// Single web app registration in prmassist-sgs ("portal-messaging"), shared by all three apps.
const SGS_CONFIG = {
  apiKey: "AIzaSyBhhj8Xryk7V-0RgE4EiPT56DDsCXqbMuE",
  authDomain: "prmassist-sgs.firebaseapp.com",
  databaseURL: "https://prmassist-sgs-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "prmassist-sgs",
  storageBucket: "prmassist-sgs.firebasestorage.app",
  messagingSenderId: "861926823777",
  appId: "1:861926823777:web:2a3e0a985a6ef2817c9720",
  measurementId: "G-6C3BTB4BPG",
};

const TARGETS = {
  // default (unnamed) app — src/utils/firebase.js
  portal: {
    live: {
      apiKey: "AIzaSyCHy8uzRY4M1JD6SQVwHDqj07a4tzjYLho",
      authDomain: "prmassist-portal.firebaseapp.com",
      databaseURL: "https://prmassist-portal-default-rtdb.europe-west1.firebasedatabase.app",
      projectId: "prmassist-portal",
      storageBucket: "prmassist-portal.appspot.com",
      messagingSenderId: "607857257143",
      appId: "1:607857257143:web:9e6ea157b9468935e5bb32",
      measurementId: "G-7FNG3ZFMFH",
    },
    sandbox: SANDBOX_CONFIG,
    sgs: SGS_CONFIG,
  },

  // named app "secondary" — src/utils/firebase-app.js
  secondary: {
    live: {
      apiKey: "AIzaSyBOhBofqRiwCbpK_kTF5EDThlnK5vPSr_0",
      authDomain: "prmassist1.firebaseapp.com",
      projectId: "prmassist1",
      storageBucket: "prmassist1.appspot.com",
      messagingSenderId: "32133056836",
      appId: "1:32133056836:web:65baa422ce6d91802a2f11",
      measurementId: "G-PP85ELBZHY",
    },
    sandbox: SANDBOX_CONFIG,
    sgs: SGS_CONFIG,
  },

  // named app "marketplace" — src/utils/marketplace-firebase.js
  marketplace: {
    live: {
      apiKey: "AIzaSyDhtpBF3eT2ll81KsfsSK_oQ63F-H_Tgi8",
      authDomain: "prmassist-marketplace.firebaseapp.com",
      projectId: "prmassist-marketplace",
      storageBucket: "prmassist-marketplace.firebasestorage.app",
      messagingSenderId: "4067604199",
      appId: "1:4067604199:web:928b2a19a09348e2115442",
      measurementId: "G-H1DTJVSJC4",
    },
    sandbox: SANDBOX_CONFIG,
    sgs: SGS_CONFIG,
  },
};

/**
 * Firebase Analytics supports one instance per measurementId. Outside live all
 * three apps share a single registration, so the 2nd and 3rd getAnalytics()
 * calls would throw. Analytics is therefore only enabled on live.
 */
export const ANALYTICS_ENABLED = FIREBASE_TARGET === 'live';

let bannerShown = false;

export function resolve(key) {
  const entry = TARGETS[key];
  if (!entry) {
    throw new Error(`[firebase-projects] Unknown target '${key}'`);
  }

  const config = entry[FIREBASE_TARGET];

  if (FIREBASE_TARGET !== 'live' && !bannerShown) {
    bannerShown = true;
    // eslint-disable-next-line no-console
    console.warn(
      `%c ${FIREBASE_TARGET.toUpperCase()} — Firebase project: ${config.projectId} `,
      'background:#C0392B;color:#fff;font-weight:bold;padding:3px 8px;border-radius:3px;font-size:12px'
    );
  }

  return config;
}
